/**
 * Chat feature types for the conversational AI chatbot widget.
 * These types define the data structures for messages, trades,
 * SSE streaming events, and file attachments.
 */

/** Message role in the conversation */
export type ChatMessageRole = 'user' | 'assistant' | 'error';

/** Allowed MIME types for file attachments */
export const ALLOWED_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
] as const;

export type AllowedMimeType = (typeof ALLOWED_MIME_TYPES)[number];

/** Maximum file size in bytes (10 MB) */
export const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

/** Maximum number of attachments per message */
export const MAX_ATTACHMENTS_PER_MESSAGE = 5;

/**
 * A file attachment on a chat message.
 * Files are base64-encoded for transmission to the Chat API.
 */
export interface ChatAttachment {
  /** Unique identifier for this attachment */
  id: string;
  /** Original filename */
  name: string;
  /** MIME type (validated against ALLOWED_MIME_TYPES) */
  mimeType: AllowedMimeType;
  /** File size in bytes */
  size: number;
  /** Base64-encoded file content */
  base64: string;
  /** Object URL for local preview (images only) */
  previewUrl?: string;
}

/**
 * A single chat message displayed in the conversation thread.
 * Represents both user-sent and bot-received messages.
 */
export interface ChatMessageData {
  /** Unique message identifier */
  id: string;
  /** Who sent the message */
  role: ChatMessageRole;
  /** Message text content (may contain markdown for assistant messages) */
  content: string;
  /** When the message was sent or received (ISO 8601) */
  timestamp: string;
  /** Whether this message is currently being streamed */
  isStreaming?: boolean;
  /** Trade confirmation attached to this message, if any */
  tradeConfirmation?: PendingTrade;
  /** File attachments on this message */
  attachments?: ChatAttachment[];
  /** The message's Activity_Trace: actions the agent took while generating this response */
  activities?: Activity[];
}

/**
 * The category discriminator of an activity.
 * Release values: memory_retrieval, knowledge_base_query, tool_call.
 * Reserved for future use: mcp_tool_call, agent_delegation.
 */
export type ActivityType =
  | 'memory_retrieval'
  | 'knowledge_base_query'
  | 'tool_call'
  | 'mcp_tool_call'
  | 'agent_delegation';

/** The lifecycle state of an activity. */
export type ActivityStatus = 'started' | 'completed' | 'failed';

/** A sanitized knowledge base source carried in a knowledge_base_query activity detail. */
export interface KnowledgeBaseSource {
  /** Sanitized source label (never an internal URI, bucket, ARN, or path) */
  label: string;
  /** Page number within the source, when available */
  page?: number;
}

/**
 * The optional, type-specific detail payload for an activity.
 * All fields are pre-sanitized by the Chat_Handler.
 */
export interface ActivityDetail {
  /** Matched sources (for knowledge_base_query activities) */
  sources?: KnowledgeBaseSource[];
  /** Invoked tool name (for tool_call / mcp_tool_call activities) */
  toolName?: string;
  /** Sanitized retrieved memory item labels (for memory_retrieval activities) */
  items?: { label: string }[];
  /** Delegated-to agent name (for agent_delegation activities) */
  agentName?: string;
}

/**
 * One activity in a message's Activity_Trace.
 * The status mutates in place as terminal events arrive.
 */
export interface Activity {
  /** Correlation id, identical across every event of this activity */
  id: string;
  /** Activity type; string tolerates unknown future types for generic rendering */
  type: ActivityType | string;
  /** Current lifecycle status */
  status: ActivityStatus;
  /** Human-readable label */
  label: string;
  /** Occurrence timestamp (ISO-8601 UTC) */
  ts: string;
  /** Optional type-specific detail payload */
  detail?: ActivityDetail;
}

/** Trade action direction */
export type TradeDirection = 'BUY' | 'SELL';

/**
 * A trade awaiting user confirmation.
 * Presented inline in the chat with Confirm/Cancel buttons.
 */
export interface PendingTrade {
  /** Stock ticker symbol */
  symbol: string;
  /** Buy or sell action */
  action: TradeDirection;
  /** Number of shares */
  quantity: number;
  /** Price per share */
  price: number;
  /** Position impact description (e.g., "Opens new position", "Adds to position (current: 100)") */
  impact: string;
  /** Estimated realized P&L for sells (FIFO cost basis), null for buys */
  estimatedPnl: number | null;
  /** When this confirmation expires (ISO 8601) */
  expiresAt: string;
  /** Current status of the trade confirmation */
  status?: 'pending' | 'confirmed' | 'cancelled' | 'expired';
  /** Whether this trade triggered a wash sale restriction */
  washSale?: boolean;
  /** Account ID this trade is for */
  accountId?: string;
}

/**
 * A message in the conversation history sent to the Chat API.
 * Trimmed representation without UI metadata.
 */
export interface ConversationMessage {
  /** Message author */
  role: 'user' | 'assistant';
  /** Message text content */
  content: string;
  /** File attachments (base64-encoded) */
  attachments?: Pick<ChatAttachment, 'name' | 'mimeType' | 'base64'>[];
}

/** SSE event types emitted by the Chat API */
export type SSEEventType =
  | 'text'
  | 'tool_call'
  | 'trade_confirmation'
  | 'trade_result'
  | 'done'
  | 'error'
  | 'warning'
  | 'activity';

/**
 * Server-Sent Event received from the Chat API stream.
 * Each event has a type discriminator with optional payloads.
 */
export interface SSEEvent {
  /** Event type discriminator */
  type: SSEEventType;
  /** Text content chunk (for 'text' events) */
  content?: string;
  /** Tool name (for 'tool_call' events) */
  name?: string;
  /** Tool call result (for 'tool_call' events) */
  result?: Record<string, unknown>;
  /** Trade data (for 'trade_confirmation' events) */
  trade?: PendingTrade;
  /** Whether a wash sale was detected (for 'trade_result' events) */
  wash_sale?: boolean;
  /** Error message (for 'error' events) */
  message?: string;
  /** Activity type discriminator (for 'activity' events) */
  activity_type?: string;
  /** Activity correlation id (for 'activity' events) */
  activity_id?: string;
  /** Activity lifecycle status (for 'activity' events) */
  status?: ActivityStatus;
  /** Human-readable activity label (for 'activity' events) */
  label?: string;
  /** Activity occurrence timestamp, ISO-8601 UTC (for 'activity' events) */
  ts?: string;
  /** Type-specific activity detail payload (for 'activity' events) */
  detail?: ActivityDetail;
}

